import type { ItineraryTableSource } from "../types/itineraryTable";
import type { components, operations } from "../../../api/generated";
import { resolveHttpResourceUrl } from "../../../lib/http/axios";
import type {
  DayContentSuggestion,
  DrivingEndpoint,
  DrivingRequest,
  DrivingResult,
  EnrichedContent,
  EnrichRequest,
  FlightQuery,
  FlightSearchResult,
  ItineraryDay,
  ItineraryEditor,
  ItineraryCustomerOutput,
  ItineraryCopyResult,
  ItineraryCopyTargetPage,
  ItineraryMoveTargets,
  ItineraryEditorDocument,
  ItineraryEnterResult,
  ItineraryListItem,
  ItineraryListPage,
  FinalizedBudgetQuoteVersion,
  ItineraryListResult,
  ItineraryMyShare,
  ItineraryMySharePage,
  ItineraryMyShareView,
  ItineraryMyShareViewPage,
  ItineraryInternalInteractions,
  ItineraryInternalMarkResult,
  ItineraryNlQueryParseResult,
  ItineraryNode,
  ItinerarySuggestion,
  ItinerarySegment,
  ItinerarySourcePayload,
  ItineraryValidationResult,
  MajorCandidate,
  MajorCandidateQuery,
  ParseDayRequest,
  ParsedDayDraft,
  PointCandidate,
  PointCandidateResult,
  PointCandidateQuery,
  PointDescriptionCompletionRequest,
  PointDescriptionCompletionResult,
  PublicItinerary,
  ItineraryReadonlyPreview,
  ItinerarySalesNotificationResult,
  RailwayQuery,
  RailwaySearchResult,
  RailwayTransferSearchResult,
  RailwayStationResolution,
  RailwayStationResolveRequest,
  ShareLinkResult,
  TransportOption,
  TransportSelection,
  TransportToolResultSnapshot,
  WalkingRequest,
  WalkingResult,
} from "../types";

type EditorOutputRaw = components["schemas"]["ItineraryEditorPayload-Output"];
type EditorInputRaw = components["schemas"]["ItineraryEditorPayload-Input"];
type DayOutputRaw = components["schemas"]["ItineraryDayInput-Output"];
type DayInputRaw = components["schemas"]["ItineraryDayInput-Input"];
type NodeRaw = components["schemas"]["ItineraryNodeInput"];
type SegmentOutputRaw = components["schemas"]["ItinerarySegmentInput-Output"];
type SegmentInputRaw = components["schemas"]["ItinerarySegmentInput-Input"];
type SourcePayloadOutputRaw = components["schemas"]["ItinerarySourcePayload-Output"];
type SourcePayloadInputRaw = components["schemas"]["ItinerarySourcePayload-Input"];
type SelectionOutputRaw = components["schemas"]["TransportSelection-Output"];
type SelectionInputRaw = components["schemas"]["TransportSelection-Input"];
type ToolSnapshotOutputRaw = components["schemas"]["TransportToolResultSnapshot-Output"];
type ToolSnapshotInputRaw = components["schemas"]["TransportToolResultSnapshot-Input"];

export type EditorResponseRaw = components["schemas"]["app__modules__itinerary__schemas__EditorResponse"];
export type EnterResponseRaw = components["schemas"]["EnterResponse"];
export type ListResponseRaw = components["schemas"]["ListResponse"];
export type ValidateResponseRaw = components["schemas"]["ValidateResponse"];
export type PointCandidateResponseRaw = components["schemas"]["PointCandidateResponse"];
export type MajorCandidateResponseRaw = components["schemas"]["MajorCandidateResponse"];
export type RailwaySearchResponseRaw = components["schemas"]["RailwaySearchResponse"];
export type RailwayTransferSearchResponseRaw = components["schemas"]["RailwayTransferSearchResponse"];
export type RailwayStationResolveResponseRaw = components["schemas"]["RailwayStationResolveResponse"];
export type FlightSearchResponseRaw = components["schemas"]["FlightSearchResponse"];
export type DrivingResponseRaw = components["schemas"]["DrivingResponse"];
export type WalkingResponseRaw = components["schemas"]["WalkingResponse"];
export type ParsedDayResponseRaw = components["schemas"]["ParsedDayResponse"];
export type EnrichedContentResponseRaw = components["schemas"]["EnrichedContentResponse"];
export type ShareLinkResponseRaw = components["schemas"]["ShareLinkResponse"];
export type PublicItineraryResponseRaw = components["schemas"]["PublicItineraryResponse"];
export type ItineraryCustomerOutputResponseRaw = components["schemas"]["CourseRouteResponse_ItineraryCustomerOutputDTO_"];
export type ItineraryReadonlyPreviewResponseRaw = components["schemas"]["ItineraryReadonlyPreviewResponse"];
export type ItineraryListItemRaw = components["schemas"]["ItineraryListItem"];
export type ItineraryListPageRaw = components["schemas"]["ItineraryListPage"];
export type FinalizedBudgetQuoteVersionsResponseRaw = components["schemas"]["FinalizedBudgetQuoteVersionsResponse"];
export type ItineraryNlQueryParseMutationRaw = components["schemas"]["ItineraryNlQueryMutation"];
export type ItineraryNlQueryParseResultRaw = components["schemas"]["ItineraryNlQueryResult"];
export type ItineraryInternalInteractionsResponseRaw = components["schemas"]["ItineraryInternalInteractionsResponse"];
export type ItineraryInternalMarkResponseRaw = components["schemas"]["ItineraryInternalMarkResponse"];
export type ItinerarySuggestionResponseRaw = components["schemas"]["ItinerarySuggestionResponse"];
export type ItineraryCopyTargetResponseRaw = components["schemas"]["ItineraryCopyTargetSuccessResponse"];
export type ItineraryCopyResponseRaw = components["schemas"]["ItineraryCopySuccessResponse"];
export type ItineraryMoveResponseRaw = components["schemas"]["ItineraryMoveSuccessResponse"];
export type ItineraryMoveTargetsResponseRaw = components["schemas"]["ItineraryMoveTargetSuccessResponse"];
export type ItineraryMySharePageRaw = components["schemas"]["SharePage"];
export type ItineraryMyShareViewPageRaw = components["schemas"]["ShareViewPage"];
export type ItinerarySalesNotificationRaw = components["schemas"]["CustomizationDispatcherNotificationSummary"];

type PointQueryRaw = NonNullable<
  operations["get_point_candidates_itineraries_candidates_points_get"]["parameters"]["query"]
>;
type MajorQueryRaw = NonNullable<
  operations["get_major_candidates_itineraries_candidates_majors_get"]["parameters"]["query"]
>;
type RailwayQueryRaw = NonNullable<
  operations["get_railway_options_itineraries_transport_railway_get"]["parameters"]["query"]
>;
type RailwayStationResolveRequestRaw = components["schemas"]["RailwayStationResolveRequest"];
type FlightQueryRaw = NonNullable<
  operations["get_flight_options_itineraries_transport_flights_get"]["parameters"]["query"]
>;
type SaveRequestRaw = components["schemas"]["ItinerarySaveRequest"];
type ValidateRequestRaw = components["schemas"]["ItineraryValidateRequest"];
type DrivingRequestRaw = components["schemas"]["DrivingRequest"];
type WalkingRequestRaw = components["schemas"]["WalkingRequest"];
type ParseDayRequestRaw = components["schemas"]["ParseDayRequest"];
type EnrichRequestRaw = components["schemas"]["EnrichRequest"];

export type PointDescriptionCompletionRequestRaw = components["schemas"]["PointDescriptionRequest"];
type PointDescriptionCompletionResultRaw = components["schemas"]["PointDescriptionBatchResult"];
export type PointDescriptionCompletionResponseRaw = components["schemas"]["PointDescriptionResponse"];

/** 映射当前员工创建的单条分享。 */
export function mapItineraryMyShare(
  raw: components["schemas"]["ShareListItem"],
): ItineraryMyShare {
  return {
    id: raw.id,
    itinerary_id: raw.itinerary_id,
    title: raw.title,
    created_at: raw.created_at,
    expires_at: raw.expires_at,
    status: raw.status,
    view_count: raw.view_count,
    url: raw.url,
    view_type: raw.view_type ?? "detail",
  };
}

/** 映射当前员工的分享分页结果。 */
export function mapItineraryMySharePage(raw: ItineraryMySharePageRaw): ItineraryMySharePage {
  return {
    items: raw.items.map(mapItineraryMyShare),
    total: raw.total,
    page: raw.page,
    page_size: raw.page_size,
  };
}

/** 映射单条分享查看事件。 */
export function mapItineraryMyShareView(
  raw: components["schemas"]["ShareViewItem"],
): ItineraryMyShareView {
  return {
    id: raw.id,
    wechat_user_id: raw.wechat_user_id,
    nickname: raw.nickname,
    avatar_url: raw.avatar_url,
    viewed_at: raw.viewed_at,
    visitor_ip: raw.visitor_ip ?? null,
  };
}

/** 映射指定分享的查看事件分页结果。 */
export function mapItineraryMyShareViewPage(
  raw: ItineraryMyShareViewPageRaw,
): ItineraryMyShareViewPage {
  return {
    items: raw.items.map(mapItineraryMyShareView),
    total: raw.total,
    page: raw.page,
    page_size: raw.page_size,
  };
}

/** 映射一条行程内部建议或回复。 */
export function mapItinerarySuggestion(
  raw: components["schemas"]["ItinerarySuggestionItem"],
): ItinerarySuggestion {
  return {
    id: raw.id,
    authorUserId: raw.author_user_id,
    authorName: raw.author_name,
    authorDeptName: raw.author_dept_name ?? null,
    parentId: raw.parent_id ?? null,
    content: raw.content,
    createdAt: raw.created_at,
  };
}

/** 映射行程内部互动总数、个人状态与建议历史。 */
export function mapItineraryInternalInteractions(
  raw: components["schemas"]["ItineraryInternalInteractions"],
): ItineraryInternalInteractions {
  return {
    suggestionCount: raw.suggestion_count,
    blacklistCount: raw.blacklist_count,
    favoriteCount: raw.favorite_count,
    blacklistedByMe: raw.blacklisted_by_me,
    favoritedByMe: raw.favorited_by_me,
    suggestions: (raw.suggestions ?? []).map(mapItinerarySuggestion),
  };
}

/** 映射拉黑或收藏切换后的服务端事实。 */
export function mapItineraryInternalMarkResult(
  raw: components["schemas"]["ItineraryInternalMarkResult"],
): ItineraryInternalMarkResult {
  return {
    markType: raw.mark_type,
    selected: raw.selected,
    total: raw.total,
  };
}

/** 将后端编辑器聚合映射为前端状态。 */
export function mapEditorDocument(raw: components["schemas"]["ItineraryEditorResult"]): ItineraryEditorDocument {
  return {
    revision: raw.revision,
    ...(raw.persisted === undefined ? {} : { persisted: raw.persisted }),
    updatedAt: raw.updated_at ?? undefined,
    payload: mapEditor(raw.payload),
    requiresPersistedNormalization: raw.requires_persisted_normalization,
    previewDegraded: raw.preview_degraded,
    previewSkippedSegmentCount: raw.preview_skipped_segment_count,
  };
}

/** 将前端快照映射为完整替换请求。 */
export function mapSaveRequest(request: ItineraryEditorDocument): SaveRequestRaw {
  return { revision: request.revision, payload: mapEditorInput(request.payload) };
}

/** 将前端快照映射为校验请求。 */
export function mapValidateRequest(editor: ItineraryEditor): ValidateRequestRaw {
  return { payload: mapEditorInput(editor) };
}

/** 映射需求进入结果。 */
export function mapEnterResult(raw: components["schemas"]["ItineraryEnterResult"]): ItineraryEnterResult {
  return {
    created: raw.created,
    context: {
      demandId: raw.context.demand_id,
      projectId: raw.context.project_id,
      projectName: raw.context.project_name,
      projectCode: raw.context.project_code,
      durationDays: raw.context.duration_days,
      departureStartDate: raw.context.departure_start_date,
      currentItineraryId: raw.context.current_itinerary_id,
    },
    editor: mapEditorDocument(raw.editor),
  };
}

/** 映射同需求行程列表。 */
export function mapItineraryList(raw: components["schemas"]["ItineraryListResult"]): ItineraryListResult {
  return {
    demandId: raw.demand_id,
    items: (raw.items ?? []).map((item) => ({
      id: item.id,
      title: item.title,
      startDate: item.start_date,
      endDate: item.end_date,
      status: item.status,
      stage: item.stage,
      isCurrent: item.is_current,
      updatedAt: item.updated_at,
    })),
  };
}

/** 统一映射复制和移动候选的版本标识及当前关联状态。 */
function mapItineraryTarget(item: components["schemas"]["ItineraryCopyTargetItem"]) {
  return {
    demandId: item.demand_id, projectId: item.project_id, projectCode: item.project_code,
    versionNumber: item.version_number, salesUserName: item.sales_user_name ?? undefined,
    dispatcherUserName: item.dispatcher_user_name ?? undefined,
    projectTitle: item.project_title, hasItinerary: item.has_itinerary,
  };
}

/** 映射当前定制内的移动候选及预期来源。 */
export function mapItineraryMoveTargets(
  raw: components["schemas"]["ItineraryMoveTargets"],
): ItineraryMoveTargets {
  return { sourceDemandId: raw.source_demand_id, items: (raw.items ?? []).map(mapItineraryTarget) };
}

/** 映射定制版本前 50 条复制候选。 */
export function mapItineraryCopyTargetPage(
  raw: components["schemas"]["ItineraryCopyTargetPage"],
): ItineraryCopyTargetPage {
  return {
    items: (raw.items ?? []).map(mapItineraryTarget),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射复制成功后的新行程导航信息。 */
export function mapItineraryCopyResult(
  raw: components["schemas"]["ItineraryCopyResult"],
): ItineraryCopyResult {
  return {
    itineraryId: raw.itinerary_id,
    projectId: raw.project_id,
    sourceDemandId: raw.source_demand_id,
    title: raw.title ?? undefined,
  };
}

/** 映射服务端校验结果。 */
export function mapValidationResult(
  raw: components["schemas"]["ItineraryValidateResult"],
): ItineraryValidationResult {
  return { valid: raw.valid, revision: raw.revision, issues: raw.issues ?? [] };
}

/** 映射候选查询参数。 */
export function mapPointCandidateQuery(query: PointCandidateQuery): PointQueryRaw {
  const keywordSearch = (query.kind === "point" || query.kind === "stay_area") && Boolean(query.keyword?.trim());
  return {
    kind: query.kind,
    ...(query.recommendation ? {
      recommendation: true, anchor_ref_type: query.anchorRefType, anchor_ref_id: query.anchorRefId,
    } : {}),
    ...(query.railwayScope ? { railway_scope: query.railwayScope } : {}),
    ...(query.railwayRegionStationId ? { railway_region_station_id: query.railwayRegionStationId } : {}),
    include_train_overnight: query.includeTrainOvernight,
    keyword: query.keyword,
    city: query.city,
    ...(query.provinceNames?.length ? { province_names: query.provinceNames } : {}),
    ...(!keywordSearch ? { page: query.page ?? 1, page_size: query.pageSize ?? 20 } : {}),
  };
}

/** 映射候选列表，普通浏览保留服务端分页信息。 */
export function mapPointCandidatePage(raw: PointCandidateResponseRaw["data"]): PointCandidateResult {
  return {
    items: (raw.items ?? []).map(mapPointCandidate),
    total: raw.total,
    ...("page" in raw ? { page: raw.page, pageSize: raw.page_size } : {}),
  };
}

/** 映射专业候选查询参数。 */
export function mapMajorCandidateQuery(query: MajorCandidateQuery): MajorQueryRaw {
  return { keyword: query.keyword, limit: query.limit ?? 20 };
}

/** 映射专业候选。 */
export function mapMajorCandidates(raw: components["schemas"]["MajorCandidate"][]): MajorCandidate[] {
  return raw.map((item) => ({ id: item.id, name: item.name, category: item.category }));
}

/** 映射火车查询参数。 */
export function mapRailwayQuery(query: RailwayQuery): RailwayQueryRaw {
  return {
    from_station_ids: query.fromStationIds,
    to_station_ids: query.toStationIds,
    date: query.date,
    train_codes: query.trainCodes,
    details: query.details ?? false,
    query_scope: query.queryScope,
    purpose: query.purpose,
  };
}

/** 映射业务地点到官方火车站的解析请求。 */
export function mapRailwayStationResolveRequest(
  request: RailwayStationResolveRequest,
): RailwayStationResolveRequestRaw {
  return {
    origin: mapRailwayEndpoint(request.origin),
    destination: mapRailwayEndpoint(request.destination),
  };
}

/** 映射业务地点的官方火车站解析结果。 */
export function mapRailwayStationResolution(
  raw: components["schemas"]["RailwayStationResolveResult"],
): RailwayStationResolution {
  const mapMatch = (match: components["schemas"]["RailwayStationMatch"] ) => ({
    endpointName: match.endpoint_name,
    matchedKeyword: match.matched_keyword,
    stations: (match.stations ?? []).map((station) => ({
      refId: station.ref_id,
      name: station.name,
    })),
  });
  return { origin: mapMatch(raw.origin), destination: mapMatch(raw.destination) };
}

/** 映射火车查询结果。 */
export function mapRailwayResult(
  raw: components["schemas"]["RailwaySearchResult"],
): RailwaySearchResult {
  return {
    requestedDate: raw.requested_date,
    queryDate: raw.query_date,
    isReference: raw.is_reference,
    referenceDate: raw.reference_date,
    options: raw.options.map(mapTransportOption),
    regionQuery: raw.region_query,
    originStation: raw.origin_station ? { refId: raw.origin_station.ref_id, name: raw.origin_station.name } : null,
    destinationStation: raw.destination_station ? { refId: raw.destination_station.ref_id, name: raw.destination_station.name } : null,
  };
}

/** 映射航班查询参数。 */
export function mapFlightQuery(query: FlightQuery): FlightQueryRaw {
  return { origin: query.origin, destination: query.destination, dep_date: query.departureDate };
}

/** 映射航班查询结果。 */
export function mapFlightResult(raw: components["schemas"]["FlightSearchResult"]): FlightSearchResult {
  return {
    requestedDate: raw.requested_date,
    queryDate: raw.query_date,
    isReference: raw.is_reference,
    referenceDate: raw.reference_date,
    referenceReason: raw.reference_reason,
    options: raw.options.map(mapTransportOption),
  };
}

/** 映射驾车请求。 */
export function mapDrivingRequest(request: DrivingRequest): DrivingRequestRaw {
  return { origin: mapDrivingEndpoint(request.origin), destination: mapDrivingEndpoint(request.destination) };
}

/** 映射驾车结果。 */
export function mapDrivingResult(raw: components["schemas"]["DrivingResult"]): DrivingResult {
  return {
    distanceKm: raw.distance_km,
    rawDurationMinutes: raw.raw_duration_minutes,
    businessDurationMinutes: raw.business_duration_minutes,
    tollsYuan: raw.tolls_yuan,
    tollDistanceKm: raw.toll_distance_km,
    tollRoad: raw.toll_road,
    source: raw.source,
  };
}

/** 映射步行请求。 */
export function mapWalkingRequest(request: WalkingRequest): WalkingRequestRaw {
  return { origin: mapDrivingEndpoint(request.origin), destination: mapDrivingEndpoint(request.destination) };
}

/** 映射步行结果。 */
export function mapWalkingResult(raw: components["schemas"]["WalkingResult"]): WalkingResult {
  return {
    distanceKm: raw.distance_km,
    rawDurationMinutes: raw.raw_duration_minutes,
    businessDurationMinutes: raw.business_duration_minutes,
    source: raw.source,
  };
}

/** 映射单日 AI 解析请求。 */
export function mapParseDayRequest(request: ParseDayRequest): ParseDayRequestRaw {
  return {
    day_id: request.dayId,
    prompt: request.prompt,
    current_day: mapDayInput(request.currentDay),
    current_segments: request.currentSegments.map(mapSegmentInput),
    context: request.context
      ? {
          itinerary_title: request.context.itineraryTitle,
          itinerary_summary: request.context.itinerarySummary,
          customer_service_standards: request.context.customerServiceStandards,
          customer_notices: request.context.customerNotices,
        }
      : request.context,
  };
}

/** 映射单日 AI 草稿。 */
export function mapParsedDay(raw: components["schemas"]["ParsedDayDraft"]): ParsedDayDraft {
  return { day: mapDay(raw.day), segments: (raw.segments ?? []).map(mapSegment) };
}

/** 映射文案生成请求。 */
export function mapEnrichRequest(request: EnrichRequest): EnrichRequestRaw {
  const targetDay = request.dayId
    ? request.editor.days.find((day) => day.id === request.dayId)
    : undefined;
  return {
    scope: request.scope,
    day_id: request.dayId,
    summary_slot: request.summarySlot,
    compose_points: request.composePoints ?? false,
    ...(request.summarySlot && targetDay?.stayAreaName
      ? { day_stay_area_name: targetDay.stayAreaName }
      : {}),
    editor: mapEditorInput(request.editor),
  };
}

/** 映射文案建议。 */
export function mapEnrichedContent(raw: components["schemas"]["EnrichedContent"]): EnrichedContent {
  return { days: (raw.days ?? []).map(mapDaySuggestion) };
}

/** 映射当前日点位介绍补全请求，未提供的筛选项不发送。 */
export function mapPointDescriptionCompletionRequest(
  request: PointDescriptionCompletionRequest,
): PointDescriptionCompletionRequestRaw {
  return {
    ...(typeof request.dayId === "string" ? { day_id: request.dayId } : {}),
    ...(Array.isArray(request.nodeIds) ? { node_ids: request.nodeIds } : {}),
    overwrite: request.overwrite ?? false,
    ...(request.editor ? { editor: mapEditorInput(request.editor) } : {}),
  };
}

/** 将逐点补全结果映射为前端领域模型。 */
export function mapPointDescriptionCompletionResult(
  raw: PointDescriptionCompletionResultRaw,
): PointDescriptionCompletionResult {
  return {
    status: raw.status,
    requestedCount: raw.total_count,
    successCount: raw.generated_count,
    failedCount: raw.failed_count,
    skippedCount: raw.skipped_count,
    items: (raw.items ?? []).map((item) => ({
      nodeId: item.node_id,
      status: item.status,
      description: item.description,
      errorCode: item.error_code,
    })),
  };
}

/** 映射分享链接。 */
export function mapShareLink(raw: components["schemas"]["ShareLinkResult"]): ShareLinkResult {
  return { token: raw.token, url: raw.url, expiresAt: raw.expires_at };
}

/** 映射脱敏公开行程。 */
export function mapPublicItinerary(raw: components["schemas"]["PublicItinerary"]): PublicItinerary {
  return {
    title: raw.title,
    startDate: raw.start_date,
    endDate: raw.end_date,
    summary: raw.summary,
    customerServiceStandards: raw.customer_service_standards,
    customerNotices: raw.customer_notices,
    pdfHeadImageUrls: (raw.pdf_head_image_urls ?? []).map(resolveHttpResourceUrl),
    groupNumber: null,
    hotels: [],
    days: (raw.days ?? []).map((day) => ({
      dayNumber: day.day_number,
      dayTitle: day.day_title,
      dayDate: day.day_date,
      stayAreaName: day.stay_area_name,
      legacyCompatibility: day.legacy_compatibility ?? false,
      morningSummary: day.morning_summary,
      morningSummaryComposed: day.morning_summary_composed ?? false,
      fullDaySummary: day.full_day_summary,
      fullDaySummaryComposed: day.full_day_summary_composed ?? false,
      afternoonSummary: day.afternoon_summary,
      afternoonSummaryComposed: day.afternoon_summary_composed ?? false,
      eveningSummary: day.evening_summary,
      eveningSummaryComposed: day.evening_summary_composed ?? false,
      summary: day.summary,
      isFullDay: day.is_full_day,
      nodes: (day.nodes ?? []).map((node) => ({
        nodeType: node.node_type,
        timeSlot: node.time_slot,
        sortOrder: node.sort_order,
        title: node.title,
        description: node.description,
        imageUrl: node.image_url ?? null,
        categoryLabel: node.category_label ?? null,
        planStartAt: node.plan_start_at,
        planEndAt: node.plan_end_at,
      })),
      segments: (day.segments ?? []).map(mapPublicSegment),
      parallelTracks: (day.parallel_tracks ?? []).map((track) => ({
        title: track.title,
        nodes: (track.nodes ?? []).map((node) => ({
          nodeType: node.node_type,
          timeSlot: node.time_slot,
          sortOrder: node.sort_order,
          title: node.title,
          description: node.description,
          imageUrl: node.image_url ?? null,
          categoryLabel: node.category_label ?? null,
          planStartAt: node.plan_start_at,
          planEndAt: node.plan_end_at,
        })),
        segments: (track.segments ?? []).map(mapPublicSegment),
      })),
    })),
  };
}

/** 映射确认件提供的对客团号与酒店安全投影。 */
export function mapItineraryCustomerOutput(
  raw: components["schemas"]["ItineraryCustomerOutputDTO"],
): ItineraryCustomerOutput {
  return {
    groupNumber: raw.group_number,
    hotels: (raw.hotels ?? []).map((hotel) => ({
      hotelName: hotel.hotel_name,
      address: hotel.address,
      dayNumbers: hotel.day_numbers ?? [],
      serviceDates: hotel.service_dates ?? [],
      rooms: (hotel.rooms ?? []).map((room) => ({
        roomName: room.room_name,
        quantity: room.quantity,
        billingCount: room.billing_count,
        unit: room.unit,
      })),
    })),
  };
}

/** 映射不可作为保存输入的历史只读预览。 */
export function mapItineraryReadonlyPreview(
  raw: components["schemas"]["ItineraryReadonlyPreview"],
): ItineraryReadonlyPreview {
  return {
    itinerary: mapPublicItinerary(raw.itinerary),
    warnings: raw.warnings ?? [],
    readOnly: true,
  };
}

/** 映射公开交通线段；旧契约仅有组合名称时也保留可读端点。 */
function mapPublicSegment(segment: components["schemas"]["PublicItinerarySegment"]): PublicItinerary["days"][number]["segments"][number] {
  const [fromTitle, toTitle] = segment.name.split(" - ", 2);
  return {
    name: segment.name,
    fromTitle: segment.from_title ?? fromTitle ?? null,
    toTitle: segment.to_title ?? toTitle ?? null,
    timeSlot: segment.time_slot,
    transportMode: segment.transport_mode,
    isPending: segment.is_pending ?? false,
    isInterDay: segment.is_inter_day ?? false,
    hasStayEndpoint: segment.has_stay_endpoint ?? false,
    routeCode: segment.route_code,
    departureAt: segment.departure_at,
    arrivalAt: segment.arrival_at,
    distanceKm: segment.distance_km,
    durationMinutes: segment.duration_minutes,
    description: segment.description,
    primaryTransport: segment.primary_transport ? mapPublicTransportOption(segment.primary_transport) : null,
    backupTransports: (segment.backup_transports ?? []).map(mapPublicTransportOption),
  };
}

/** 映射不含 provider 与 option_id 的公开交通方案。 */
function mapPublicTransportOption(option: components["schemas"]["PublicTransportOption"]): NonNullable<PublicItinerary["days"][number]["segments"][number]["primaryTransport"]> {
  return {
    label: option.label,
    routeCode: option.route_code,
    seatClass: option.seat_class,
    cabinClass: option.cabin_class,
    departureAt: option.departure_at,
    arrivalAt: option.arrival_at,
    distanceKm: option.distance_km,
    durationMinutes: option.duration_minutes,
  };
}

/** 映射编辑器主体。 */
function mapEditor(raw: EditorOutputRaw): ItineraryEditor {
  return {
    id: raw.id,
    projectId: raw.project_id,
    sourceDemandId: raw.source_demand_id,
    proposalArticleId: raw.proposal_article_id,
    pricedArticleId: raw.priced_article_id,
    title: raw.title,
    startDate: raw.start_date,
    endDate: raw.end_date,
    status: raw.status,
    stage: raw.stage,
    maintenanceMode: raw.maintenance_mode ?? "manual",
    thirdPartyItineraryAttachment: raw.third_party_itinerary_attachment,
    markdownSource: raw.markdown_source,
    isConfirmed: raw.is_confirmed,
    isReviewing: raw.status === "reviewing",
    summary: raw.summary,
    customerServiceStandards: raw.customer_service_standards,
    professionSettings: raw.profession_settings
      ? {
          mainMajorId: raw.profession_settings.main_major_id,
          majors: raw.profession_settings.majors.map((major) => ({
            id: major.id,
            name: major.name,
          })),
        }
      : raw.profession_settings,
    customerNotices: raw.customer_notices,
    days: (raw.days ?? []).map(mapDay),
    segments: (raw.segments ?? []).map(mapSegment),
  };
}

/** 映射编辑器请求主体。 */
function mapEditorInput(editor: ItineraryEditor): EditorInputRaw {
  return {
    id: editor.id,
    project_id: editor.projectId,
    source_demand_id: editor.sourceDemandId,
    proposal_article_id: editor.proposalArticleId,
    priced_article_id: editor.pricedArticleId,
    title: editor.title,
    start_date: editor.startDate,
    end_date: editor.endDate,
    status: editor.status,
    stage: editor.stage,
    maintenance_mode: editor.maintenanceMode ?? "manual",
    third_party_itinerary_attachment: editor.thirdPartyItineraryAttachment,
    markdown_source: editor.markdownSource,
    is_confirmed: editor.isConfirmed,
    is_reviewing: editor.status === "reviewing",
    summary: editor.summary,
    customer_service_standards: editor.customerServiceStandards,
    profession_settings: editor.professionSettings
      ? {
          main_major_id: editor.professionSettings.mainMajorId,
          majors: editor.professionSettings.majors.map((major) => ({
            id: major.id,
            name: major.name,
          })),
        }
      : editor.professionSettings,
    customer_notices: editor.customerNotices,
    days: editor.days.map(mapDayInput),
    segments: editor.segments.map(mapSegmentInput),
  };
}

/** 映射单日数据。 */
function mapDay(raw: DayOutputRaw): ItineraryDay {
  return {
    id: raw.id,
    dayNumber: raw.day_number,
    dayTitle: raw.day_title,
    dayDate: raw.day_date,
    stayAreaId: raw.stay_area_id,
    stayAreaName: raw.stay_area_name,
    legacyCompatibility: raw.legacy_compatibility ?? false,
    morningSummary: raw.morning_summary,
    morningSummarySource: raw.morning_summary_source ?? "ai_generated",
    fullDaySummary: raw.full_day_summary,
    fullDaySummarySource: raw.full_day_summary_source,
    afternoonSummary: raw.afternoon_summary,
    afternoonSummarySource: raw.afternoon_summary_source ?? "ai_generated",
    eveningSummary: raw.evening_summary,
    eveningSummarySource: raw.evening_summary_source ?? "ai_generated",
    summary: raw.summary,
    summarySource: raw.summary_source ?? "ai_generated",
    isFullDay: raw.is_full_day,
    parallelTracks: raw.parallel_tracks
      ? {
          version: raw.parallel_tracks.version,
          tracks: (raw.parallel_tracks.tracks ?? []).map((track) => ({
            id: track.id,
            title: track.title,
            majorId: track.major_id,
            nodeIds: track.node_ids ?? [],
            segmentIds: track.segment_ids ?? [],
          })),
        }
      : raw.parallel_tracks,
    nodes: (raw.nodes ?? []).map(mapNode),
  };
}

/** 映射单日请求数据。 */
function mapDayInput(day: ItineraryDay): DayInputRaw {
  return {
    id: day.id,
    day_number: day.dayNumber,
    day_title: day.dayTitle,
    day_date: day.dayDate,
    stay_area_id: day.stayAreaId,
    legacy_compatibility: day.legacyCompatibility,
    morning_summary: day.morningSummary,
    morning_summary_source: day.morningSummarySource ?? "ai_generated",
    ...(day.fullDaySummary !== undefined ? { full_day_summary: day.fullDaySummary } : {}),
    ...(day.fullDaySummarySource !== undefined ? { full_day_summary_source: day.fullDaySummarySource } : {}),
    afternoon_summary: day.afternoonSummary,
    afternoon_summary_source: day.afternoonSummarySource ?? "ai_generated",
    evening_summary: day.eveningSummary,
    evening_summary_source: day.eveningSummarySource ?? "ai_generated",
    summary: day.summary,
    summary_source: day.summarySource ?? "ai_generated",
    is_full_day: day.isFullDay,
    parallel_tracks: day.parallelTracks
      ? {
          version: day.parallelTracks.version,
          tracks: day.parallelTracks.tracks.map((track) => ({
            id: track.id,
            title: track.title,
            major_id: track.majorId,
            node_ids: track.nodeIds,
            segment_ids: track.segmentIds,
          })),
        }
      : day.parallelTracks,
    nodes: day.nodes.map(mapNodeInput),
  };
}

/** 映射行程节点。 */
function mapNode(raw: NodeRaw): ItineraryNode {
  return {
    id: raw.id,
    timeSlot: raw.time_slot,
    sortOrder: raw.sort_order,
    nodeType: raw.node_type,
    pointSpuId: raw.point_spu_id,
    stayAreaId: raw.stay_area_id,
    organizationSiteId: raw.organization_site_id,
    title: raw.title,
    description: raw.description,
    imageUrl: raw.image_url ?? null,
    categoryLabel: raw.category_label ?? null,
    railwayRegionName: raw.railway_region_name ?? null,
    planStartAt: raw.plan_start_at,
    planEndAt: raw.plan_end_at,
  };
}

/** 映射节点请求数据。 */
function mapNodeInput(node: ItineraryNode): NodeRaw {
  return {
    id: node.id,
    time_slot: node.timeSlot,
    sort_order: node.sortOrder,
    node_type: node.nodeType,
    point_spu_id: node.pointSpuId,
    stay_area_id: node.stayAreaId,
    organization_site_id: node.organizationSiteId,
    title: node.title,
    description: node.description,
    plan_start_at: node.planStartAt,
    plan_end_at: node.planEndAt,
  };
}

/** 映射路线段。 */
function mapSegment(raw: SegmentOutputRaw): ItinerarySegment {
  return {
    id: raw.id,
    startDayId: raw.start_day_id,
    endDayId: raw.end_day_id,
    routeSeq: raw.route_seq,
    majorId: raw.major_id,
    routeType: raw.route_type,
    transportMode: raw.transport_mode,
    fromNodeId: raw.from_node_id,
    toNodeId: raw.to_node_id,
    transportSpuId: raw.transport_spu_id,
    transportSkuId: raw.transport_sku_id,
    busSpuId: raw.bus_spu_id,
    busSkuId: raw.bus_sku_id,
    routeCode: raw.route_code,
    departureAt: raw.departure_at,
    arrivalAt: raw.arrival_at,
    distanceKm: raw.distance_km,
    durationMinutes: raw.duration_minutes,
    segmentDescription: raw.segment_description,
    sourceType: raw.source_type,
    sourcePayload: raw.source_payload ? mapSourcePayload(raw.source_payload) : raw.source_payload,
    remark: raw.remark,
  };
}

/** 映射路线段请求数据。 */
function mapSegmentInput(segment: ItinerarySegment): SegmentInputRaw {
  return {
    id: segment.id,
    start_day_id: segment.startDayId,
    end_day_id: segment.endDayId,
    route_seq: segment.routeSeq,
    major_id: segment.majorId,
    route_type: segment.routeType,
    transport_mode: segment.transportMode,
    from_node_id: segment.fromNodeId,
    to_node_id: segment.toNodeId,
    transport_spu_id: segment.transportSpuId,
    transport_sku_id: segment.transportSkuId,
    bus_spu_id: segment.busSpuId,
    bus_sku_id: segment.busSkuId,
    route_code: segment.routeCode,
    departure_at: segment.departureAt,
    arrival_at: segment.arrivalAt,
    distance_km: segment.distanceKm,
    duration_minutes: segment.durationMinutes,
    segment_description: segment.segmentDescription,
    source_type: segment.sourceType,
    source_payload: segment.sourcePayload ? mapSourcePayloadInput(segment.sourcePayload) : segment.sourcePayload,
    remark: segment.remark,
  };
}

/** 映射交通来源快照。 */
function mapSourcePayload(raw: SourcePayloadOutputRaw): ItinerarySourcePayload {
  return {
    version: raw.version,
    selected: raw.selected ? mapSelection(raw.selected) : raw.selected,
    backups: (raw.backups ?? []).map(mapSelection),
    railwayOvernight: raw.railway_overnight ? { stayNodeIds: raw.railway_overnight.stay_node_ids } : undefined,
    toolResultSnapshot: raw.tool_result_snapshot
      ? mapToolSnapshot(raw.tool_result_snapshot)
      : raw.tool_result_snapshot,
    railwayTransfer: raw.railway_transfer ? {
      version: raw.railway_transfer.version,
      groupId: raw.railway_transfer.group_id,
      nodeIds: raw.railway_transfer.node_ids,
      generatedNodeIds: raw.railway_transfer.generated_node_ids,
      segmentIds: raw.railway_transfer.segment_ids,
      chainSignature: raw.railway_transfer.chain_signature,
      plans: raw.railway_transfer.plans.map((plan) => ({
        legs: plan.legs.map(mapSelection), snapshot: mapToolSnapshot(plan.snapshot),
      })),
    } : undefined,
  };
}

/** 映射交通来源请求数据。 */
function mapSourcePayloadInput(payload: ItinerarySourcePayload): SourcePayloadInputRaw {
  return {
    version: payload.version,
    selected: payload.selected ? mapSelectionInput(payload.selected) : payload.selected,
    backups: payload.backups.map(mapSelectionInput),
    railway_overnight: payload.railwayOvernight ? { stay_node_ids: payload.railwayOvernight.stayNodeIds } : undefined,
    tool_result_snapshot: payload.toolResultSnapshot
      ? mapToolSnapshotInput(payload.toolResultSnapshot)
      : payload.toolResultSnapshot,
    railway_transfer: payload.railwayTransfer ? {
      version: payload.railwayTransfer.version,
      group_id: payload.railwayTransfer.groupId,
      node_ids: payload.railwayTransfer.nodeIds,
      generated_node_ids: payload.railwayTransfer.generatedNodeIds,
      segment_ids: payload.railwayTransfer.segmentIds,
      chain_signature: payload.railwayTransfer.chainSignature,
      plans: payload.railwayTransfer.plans.map((plan) => ({
        legs: plan.legs.map(mapSelectionInput), snapshot: mapToolSnapshotInput(plan.snapshot),
      })),
    } : undefined,
  };
}

/** 映射交通选项。 */
function mapSelection(raw: SelectionOutputRaw): TransportSelection {
  return {
    provider: raw.provider,
    optionId: raw.option_id,
    label: raw.label,
    routeCode: raw.route_code,
    fromName: raw.from_name,
    toName: raw.to_name,
    fromStationId: raw.from_station_id,
    toStationId: raw.to_station_id,
    price: raw.price,
    seatClass: raw.seat_class,
    cabinClass: raw.cabin_class,
    departureAt: raw.departure_at,
    arrivalAt: raw.arrival_at,
    distanceKm: raw.distance_km,
    durationMinutes: raw.duration_minutes,
  };
}

/** 映射交通选项请求数据。 */
function mapSelectionInput(selection: TransportSelection): SelectionInputRaw {
  return {
    provider: selection.provider,
    option_id: selection.optionId,
    label: selection.label,
    route_code: selection.routeCode,
    from_name: selection.fromName,
    to_name: selection.toName,
    from_station_id: selection.fromStationId,
    to_station_id: selection.toStationId,
    price: selection.price,
    seat_class: selection.seatClass,
    cabin_class: selection.cabinClass,
    departure_at: selection.departureAt,
    arrival_at: selection.arrivalAt,
    distance_km: selection.distanceKm,
    duration_minutes: selection.durationMinutes,
  };
}

/** 映射交通工具查询快照。 */
function mapToolSnapshot(raw: ToolSnapshotOutputRaw): TransportToolResultSnapshot {
  return {
    provider: raw.provider,
    queriedAt: raw.queried_at,
    queryKey: raw.query_key,
    railwayQueryScope: raw.railway_query_scope,
    options: (raw.options ?? []).map(mapSelection),
    rawDurationMinutes: raw.raw_duration_minutes,
    businessDurationMinutes: raw.business_duration_minutes,
    tollsYuan: raw.tolls_yuan,
    tollDistanceKm: raw.toll_distance_km,
    isReference: raw.is_reference,
    referenceDate: raw.reference_date,
    warnings: raw.warnings ?? [],
  };
}

/** 映射交通工具查询快照请求数据。 */
function mapToolSnapshotInput(snapshot: TransportToolResultSnapshot): ToolSnapshotInputRaw {
  return {
    provider: snapshot.provider,
    queried_at: snapshot.queriedAt,
    query_key: snapshot.queryKey,
    railway_query_scope: snapshot.railwayQueryScope,
    options: snapshot.options.map(mapSelectionInput),
    raw_duration_minutes: snapshot.rawDurationMinutes,
    business_duration_minutes: snapshot.businessDurationMinutes,
    tolls_yuan: snapshot.tollsYuan,
    toll_distance_km: snapshot.tollDistanceKm,
    is_reference: snapshot.isReference,
    reference_date: snapshot.referenceDate,
    warnings: snapshot.warnings,
  };
}

/** 映射点位候选。 */
function mapPointCandidate(raw: components["schemas"]["PointCandidate"]): PointCandidate {
  return {
    refType: raw.ref_type,
    refId: raw.ref_id,
    name: raw.name,
    shortName: raw.short_name,
    railwayRegionName: raw.railway_region_name ?? null,
    pointType: raw.point_type,
    city: raw.city,
    address: raw.address,
    headImageUrl: raw.head_image_url,
    lng: raw.lng,
    lat: raw.lat,
  };
}

/** 映射交通查询选项。 */
function mapTransportOption(raw: components["schemas"]["TransportOption"]): TransportOption {
  return {
    railway: raw.railway ? { ...raw.railway, seats: raw.railway.seats.map((seat) => ({ ...seat, count: seat.count ?? null, price: seat.price ?? null, discount: seat.discount ?? null, waitlist: seat.waitlist ?? "unknown", berths: (seat.berths ?? []).map((berth) => ({ ...berth, discount: berth.discount ?? null })) })) } : null,
    optionId: raw.option_id,
    provider: raw.provider,
    routeCode: raw.route_code,
    fromName: raw.from_name,
    toName: raw.to_name,
    fromStationId: raw.from_station_id,
    toStationId: raw.to_station_id,
    departureAt: raw.departure_at,
    arrivalAt: raw.arrival_at,
    durationMinutes: raw.duration_minutes,
    price: raw.price,
    seats: (raw.seats ?? []).map((seat) => ({
      seatClass: seat.seat_class,
      availability: seat.availability,
      price: seat.price,
    })),
  };
}

/** 映射驾车端点。 */
function mapDrivingEndpoint(endpoint: DrivingEndpoint): components["schemas"]["DrivingEndpointInput"] {
  return {
    ref_type: endpoint.refType,
    ref_id: endpoint.refId,
    transport_kind: endpoint.transportKind,
    name: endpoint.name,
    address: endpoint.address,
    province: endpoint.province,
    lng: endpoint.lng,
    lat: endpoint.lat,
  };
}

/** 映射铁路站点自动解析端点。 */
function mapRailwayEndpoint(
  endpoint: RailwayStationResolveRequest["origin"],
): components["schemas"]["RailwayEndpointInput"] {
  return {
    ref_type: endpoint.refType ?? null,
    ref_id: endpoint.refId ?? null,
    name: endpoint.name,
  };
}

/** 映射单日文案建议。 */
function mapDaySuggestion(raw: components["schemas"]["DayContentSuggestion"]): DayContentSuggestion {
  return {
    dayId: raw.day_id,
    dayTitle: raw.day_title,
    legacyCompatibility: raw.legacy_compatibility ?? false,
    morningSummary: raw.morning_summary,
    fullDaySummary: raw.full_day_summary,
    afternoonSummary: raw.afternoon_summary,
    eveningSummary: raw.evening_summary,
    summary: raw.summary,
    composedSlots: raw.composed_slots ?? [],
    emptySlots: raw.empty_slots ?? [],
    nodeDescriptions: (raw.node_descriptions ?? []).map((item) => ({
      nodeId: item.node_id,
      description: item.description,
    })),
    segmentDescriptions: (raw.segment_descriptions ?? []).map((item) => ({
      segmentId: item.segment_id,
      segmentDescription: item.segment_description,
    })),
  };
}

/** 将 generated 课程线路分页契约映射为领域模型。 */
export function mapItineraryListPage(raw: ItineraryListPageRaw): ItineraryListPage {
  return {
    items: (raw.items ?? []).map(mapItineraryListItem),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射课程线路展开的定稿核价及报价版本摘要。 */
export function mapFinalizedBudgetQuoteVersions(
  raw: components["schemas"]["FinalizedBudgetQuoteVersion"][],
): FinalizedBudgetQuoteVersion[] {
  return raw
    .map((item) => ({
      budgetOrderId: item.budget_order_id,
      itineraryId: item.itinerary_id,
      versionNo: item.version_no,
      costBudgetTotalAmount: item.cost_budget_total_amount,
      updatedAt: item.updated_at,
      customerQuoteId: normalizeOptionalText(item.customer_quote_id ?? undefined),
      customerQuoteVersionNo: item.customer_quote_version_no ?? undefined,
      customerQuoteAmount: item.customer_quote_amount ?? undefined,
    }))
    .sort((left, right) => right.versionNo - left.versionNo);
}

/** 映射课程线路销售微信通知结果。 */
export function mapItinerarySalesNotification(
  raw: ItinerarySalesNotificationRaw,
): ItinerarySalesNotificationResult {
  return {
    sentCount: raw.sent_count ?? 0,
    failedCount: raw.failed_count ?? 0,
    skippedCount: raw.skipped_count ?? 0,
    failureMessages: raw.failure_messages ?? [],
  };
}

/** 将 generated 列表项映射为领域模型。 */
function mapItineraryListItem(raw: ItineraryListItemRaw): ItineraryListItem {
  return {
    itineraryId: raw.itinerary_id,
    projectId: normalizeOptionalText(raw.project_id),
    title: normalizeOptionalText(raw.title),
    status: raw.status,
    isReviewing: raw.status === "reviewing",
    canPreview: raw.can_preview ?? true,
    projectCode: normalizeOptionalText(raw.project_code),
    demandVersionNumber: normalizeOptionalText(raw.demand_version_number),
    regionLabels: raw.region_labels ?? [],
    pointNames: raw.point_names ?? [],
    stayAreaNames: raw.stay_area_names ?? [],
    routeSummary: normalizeOptionalText(raw.route_summary),
    schoolName: normalizeOptionalText(raw.school_name),
    majorNames: raw.major_names ?? [],
    durationDays: raw.duration_days ?? undefined,
    salesUserName: normalizeOptionalText(raw.sales_user_name),
    dispatcherUserName: normalizeOptionalText(raw.dispatcher_user_name),
    companyVisitCount: raw.company_visit_count,
    intangibleHeritageCount: raw.intangible_heritage_count,
    finalizedBudgetCount: raw.finalized_budget_count ?? 0,
    finalizedQuoteCount: raw.finalized_quote_count ?? 0,
    latestFinalizedQuoteId: normalizeOptionalText(raw.latest_finalized_quote_id),
    latestFinalizedQuoteAmount: raw.latest_finalized_quote_amount == null
      ? undefined
      : normalizeOptionalText(String(raw.latest_finalized_quote_amount)),
    isFormed: raw.is_formed ?? false,
    teamId: normalizeOptionalText(raw.team_id),
    createdAt: raw.created_at,
    updatedAt: raw.updated_at ?? raw.created_at,
  };
}

/** 构建课程线路自然语言解析请求。 */
export function mapItineraryNlQueryParsePayload(query: string): ItineraryNlQueryParseMutationRaw {
  return { query: query.trim() };
}

/** 将自然语言解析结果限制在五类线路筛选字段内。 */
export function mapItineraryNlQueryParseResult(
  raw: ItineraryNlQueryParseResultRaw,
): ItineraryNlQueryParseResult {
  return {
    majorNames: raw.major_names ?? [],
    durationDays: raw.duration_days ?? undefined,
    provinceNames: raw.province_names ?? [],
    stayAreaNames: raw.stay_area_names ?? [],
    pointNames: raw.point_names ?? [],
  };
}

/** 规整可空文本。 */
function normalizeOptionalText(value: string | null | undefined): string | undefined {
  const normalized = value?.trim();
  return normalized || undefined;
}

export type ItineraryTableResponseRaw = components["schemas"]["ItineraryTableResponse"];

/** 将服务端白名单转换为共用表格输入，文档内 key 不用于保存或编辑。 */
export function mapItineraryTable(data: components["schemas"]["ItineraryTable"]): ItineraryTableSource {
  return {
    title: data.title, startDate: data.start_date,
    days: (data.days ?? []).map((day) => ({
      id: day.key, dayNumber: day.day_number, dayTitle: day.day_title, dayDate: day.day_date,
      stayAreaName: day.stay_area_name, isFullDay: day.is_full_day,
      legacyCompatibility: day.legacy_compatibility ?? false,
      morningSummary: day.morning_summary, afternoonSummary: day.afternoon_summary,
      eveningSummary: day.evening_summary, summary: day.summary,
      fullDaySummary: day.full_day_summary,
      nodes: day.nodes.map((node) => ({
        id: node.key, nodeType: node.node_type, timeSlot: node.time_slot,
        sortOrder: node.sort_order, title: node.title,
        pointSpuId: node.resource_key, stayAreaId: node.resource_key,
        organizationSiteId: node.resource_key ? Number(node.resource_key.replace("resource-", "")) : null,
      })),
      parallelTracks: day.tracks.length ? { version: 1, tracks: day.tracks.map((track) => ({
        id: track.key, majorId: track.key, title: track.title,
        nodeIds: track.node_keys, segmentIds: track.segment_keys,
      })) } : null,
    })),
    segments: (data.segments ?? []).map((segment, index) => ({
      id: segment.key, startDayId: segment.start_day_key, endDayId: segment.end_day_key,
      fromNodeId: segment.from_node_key, toNodeId: segment.to_node_key,
      transportMode: segment.transport_mode, sourceType: segment.is_pending ? "pending" : "manual",
      // 结构连接已在服务端剔除；此处不再依据展示别名推断或隐藏交通。
      routeType: "main_transport", routeSeq: index + 1,
    })),
  };
}

/** 中转响应沿用同一生成契约入口和直达车次映射，保留每腿站点与日期事实。 */
export function mapRailwayTransferResult(raw: RailwayTransferSearchResponseRaw["data"]): RailwayTransferSearchResult {
  return {
    requestedDate: raw.requested_date, queryDate: raw.query_date,
    isReference: raw.is_reference, referenceDate: raw.reference_date,
    warnings: raw.warnings ?? [],
    transfers: raw.transfers.map((item) => ({
      optionId: item.option_id, label: item.label, departureAt: item.departure_at, arrivalAt: item.arrival_at,
      durationMinutes: item.duration_minutes, waitMinutes: item.wait_minutes,
      sameStation: item.same_station, sameTrain: item.same_train,
      legs: mapRailwayResult({ requested_date: raw.requested_date, query_date: raw.query_date,
        is_reference: raw.is_reference, region_query: false, options: item.legs }).options,
    })),
  };
}
